import React, { useState, useCallback, useRef, useEffect } from "react";
import { Sparkles, Loader2, MessageCircle } from "lucide-react";
import { useReactFlow, useStore } from "reactflow";

const HighlightHandler = ({
  children,
  onHighlight,
  triggerShimmer,
  isEnabled = true,
}) => {
  const [showButtons, setShowButtons] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState("");
  const [preservedText, setPreservedText] = useState(""); // Preserve text when dialog opens
  const [isLoading, setIsLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [customQuestion, setCustomQuestion] = useState("");
  const containerRef = useRef(null);
  const isSelectingRef = useRef(false); // Track if user is actively selecting text

  // Get ReactFlow instance and zoom level
  const reactFlowInstance = useReactFlow();
  const zoom = useStore((state) => state.transform[2]);

  // Handle text selection - much simpler without custom highlighting
  const handleTextSelection = useCallback(() => {
    if (!isEnabled) return;

    const selection = window.getSelection();
    const text = selection.toString().trim();

    if (text && text.length > 0 && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();

      // Check if selection is within our container
      // Handle both forward and backward selections
      const isInContainer =
        containerRef.current &&
        (containerRef.current.contains(range.commonAncestorContainer) ||
          containerRef.current.contains(selection.anchorNode) ||
          containerRef.current.contains(selection.focusNode));

      if (containerRect && isInContainer) {
        // Validate selection rectangle - skip invalid selections
        if (rect.width === 0 && rect.height === 0) {
          // console.log("Skipping invalid selection with zero dimensions");
          return;
        }

        // Check if selection is mostly outside the visible container
        const isOutsideContainer =
          rect.bottom < containerRect.top ||
          rect.top > containerRect.bottom ||
          rect.right < containerRect.left ||
          rect.left > containerRect.right;

        if (isOutsideContainer) {
          // console.log("Skipping selection outside container bounds");
          return;
        }

        setSelectedText(text);

        // Get the current zoom level
        const currentZoom = reactFlowInstance.getViewport().zoom;

        // Position buttons at the bottom center of the selection, adjusted for zoom
        const buttonX =
          (rect.left + rect.width / 2 - containerRect.left) / currentZoom;
        const buttonHeight = 36 / currentZoom; // Adjust button height for zoom
        const spacing = 10 / currentZoom; // Spacing from selection

        // Try to position below the selection first
        let buttonY = (rect.bottom - containerRect.top) / currentZoom + spacing;

        // Check if buttons would go outside container bounds
        const containerHeight = containerRect.height / currentZoom;
        const maxY = containerHeight - buttonHeight - spacing;

        // If not enough space below, position above the selection
        if (buttonY > maxY) {
          buttonY =
            (rect.top - containerRect.top) / currentZoom -
            buttonHeight -
            spacing;

          // If still not enough space above, position at the top
          if (buttonY < spacing) {
            buttonY = spacing;
          }
        }

        // Final validation - ensure button position is reasonable
        if (isNaN(buttonX) || isNaN(buttonY) || buttonX < 0 || buttonY < 0) {
          // console.log("Skipping invalid button position:", {
          //   buttonX,
          //   buttonY,
          // });
          return;
        }

        setButtonPosition({ x: buttonX, y: buttonY });
        setShowButtons(true);
      }
    } else {
      // No selection - hide buttons
      setShowButtons(false);
      setSelectedText("");
    }
  }, [isEnabled, reactFlowInstance]);

  // Track mousedown to detect text selection intent
  const handleMouseDown = useCallback(() => {
    if (!isEnabled) return;
    // Mark that user might be starting a text selection
    isSelectingRef.current = true;
  }, [isEnabled]);

  // Handle mouse up event
  const handleMouseUp = useCallback(
    (e) => {
      if (!isEnabled) return;
      // Small delay to let the selection complete
      setTimeout(() => {
        handleTextSelection(e);
        // Clear the selection flag after a delay to allow click event to use it
        setTimeout(() => {
          isSelectingRef.current = false;
        }, 100); // Increased to ensure click event can check this flag
      }, 10);
    },
    [isEnabled, handleTextSelection]
  );

  // Prevent ReactFlow from handling clicks when text is selected
  const handleContainerClick = useCallback((e) => {
    // Check both the actual selection AND the isSelecting flag
    // The flag helps catch multiline selections where the selection might not be fully committed yet
    const selection = window.getSelection();
    const hasTextSelected = selection && selection.toString().trim().length > 0;

    // If there's text selected OR user just finished selecting, stop the click from reaching ReactFlow
    // This prevents ReactFlow from selecting the node and clearing the text selection
    if (hasTextSelected || isSelectingRef.current) {
      e.stopPropagation();
    }
  }, []);

  // Hide buttons when clicking elsewhere
  const handleDocumentClick = useCallback((e) => {
    // Ignore clicks that happen during text selection to prevent race condition
    if (isSelectingRef.current) return;

    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setShowButtons(false);
      setSelectedText("");
    }
  }, []);

  // Setup document click listener
  useEffect(() => {
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [handleDocumentClick]);

  // Recalculate button position when zoom changes
  useEffect(() => {
    if (showButtons && selectedText) {
      // Re-run text selection to update button position with new zoom
      const selection = window.getSelection();
      if (selection.toString().trim() === selectedText) {
        handleTextSelection();
      }
    }
  }, [zoom, showButtons, selectedText, handleTextSelection]); // Re-run when zoom changes

  const handleLearnMore = useCallback(async () => {
    if (selectedText && onHighlight) {
      // IMMEDIATELY trigger shimmer for instant responsiveness!
      if (triggerShimmer) {
        triggerShimmer();
      }

      setIsLoading(true);

      try {
        // Call the onHighlight function
        await onHighlight(selectedText);
      } catch (error) {
        // console.error("Learn more failed:", error);
      } finally {
        // Keep loading state for a bit after completion
        setTimeout(() => {
          setIsLoading(false);
          setShowButtons(false);
          setSelectedText("");
          // Clear the browser selection
          window.getSelection().removeAllRanges();
        }, 2500);
      }
    }
  }, [selectedText, onHighlight, triggerShimmer]);

  const handleCustomQuestion = useCallback(async () => {
    // Use preserved text if selected text is empty
    const contextText = selectedText || preservedText;

    if (customQuestion.trim() && onHighlight && contextText) {
      // Create a special object to pass both the question and context
      const questionWithContext = {
        question: customQuestion.trim(),
        context: contextText,
        isCustomQuestion: true,
      };

      // IMMEDIATELY trigger shimmer for instant responsiveness!
      if (triggerShimmer) {
        triggerShimmer();
      }

      // Close the dialog
      setShowDialog(false);
      setCustomQuestion("");

      // Clear buttons and selection
      setShowButtons(false);
      setSelectedText("");
      window.getSelection().removeAllRanges();

      try {
        // Call the onHighlight function with the custom question object
        await onHighlight(questionWithContext);
      } catch (error) {
        // console.error("Error in custom question:", error);
      }
    }
  }, [
    customQuestion,
    selectedText,
    preservedText,
    onHighlight,
    triggerShimmer,
  ]);

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleContainerClick}
      style={{
        "--shimmer-gradient":
          "linear-gradient(90deg, currentColor 0%, rgba(255, 255, 255, 0.9) 50%, currentColor 100%)",
      }}>
      <style>
        {`
          @keyframes shimmer-sweep {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(400%);
            }
          }

          @keyframes modalSlideIn {
            0% {
              opacity: 0;
              transform: scale(0.95) translateY(-10px);
            }
            100% {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
        `}
      </style>

      {children}

      {/* Selection Action Buttons - Positioned at bottom of selection */}
      {showButtons && (
        <div
          className="absolute z-50 pointer-events-auto flex gap-2"
          style={{
            left: `${buttonPosition.x}px`,
            top: `${buttonPosition.y}px`,
            transform: `translateX(-50%) scale(${
              1 / (reactFlowInstance?.getViewport()?.zoom || 1)
            })`, // Center and scale for zoom
            transformOrigin: "top center",
          }}>
          <button
            onClick={handleLearnMore}
            disabled={isLoading}
            className={`${
              isLoading
                ? "bg-green-500/70 cursor-not-allowed"
                : "bg-green-500 hover:bg-green-600"
            } text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg flex items-center space-x-1 transition-all duration-200 whitespace-nowrap`}>
            {isLoading ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <Sparkles size={12} />
                <span>Learn more</span>
              </>
            )}
          </button>

          {!isLoading && (
            <button
              onClick={() => {
                // Preserve the selected text before opening dialog
                setPreservedText(selectedText);
                setShowDialog(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg flex items-center space-x-1 transition-all duration-200 whitespace-nowrap">
              <MessageCircle size={12} />
              <span>Ask</span>
            </button>
          )}
        </div>
      )}

      {/* Custom Question Dialog */}
      {showDialog && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowDialog(false)}>
          <div
            className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              transform: "scale(1)",
              animation: "modalSlideIn 0.3s ease-out",
            }}>
            <div className="flex items-center mb-4">
              <MessageCircle className="text-blue-500 mr-3" size={24} />
              <h3 className="text-lg font-semibold text-gray-900">
                Ask a Follow Up Question
              </h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Selected text: "{selectedText || preservedText}"
            </p>

            <div className="mb-4">
              <label
                htmlFor="customQuestion"
                className="block text-sm font-medium text-gray-700 mb-2">
                Your Question:
              </label>
              <textarea
                id="customQuestion"
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (customQuestion.trim()) {
                      handleCustomQuestion();
                    }
                  } else if (e.key === "Escape") {
                    setShowDialog(false);
                    setCustomQuestion("");
                  }
                }}
                placeholder="Type your question about the selected text... (Press Enter to submit, Shift+Enter for new line)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                // autoFocus
              />
            </div>

            <div className="flex space-x-3 justify-end">
              <button
                onClick={() => {
                  setShowDialog(false);
                  setCustomQuestion("");
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={() => handleCustomQuestion()}
                disabled={!customQuestion.trim()}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors flex items-center space-x-2">
                <MessageCircle size={16} />
                <span>Submit</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HighlightHandler;
